// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type {
  DisplayTotal,
  GameResultState,
  HoleEventEntry,
  Player,
  ScoreTotal,
  TeamSummary,
} from '@/features/hole/types';

const normalizePlayerKey = (key: string | undefined | null) => key?.trim().toLowerCase() ?? '';
const isUuidLike = (value?: string) =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const hazardTypeFromName = (name?: string) => {
  const normalized = (name ?? '').toLowerCase();
  if (normalized.includes('water')) return 'water';
  if (normalized.includes('penalty')) return 'penalty';
  if (normalized.includes('bunker')) return 'bunker';
  if (normalized.includes('3 putts') || normalized.includes('three putts')) return 'threeputt';
  return null;
};

export type GameResultRow = {
  key: string;
  name: string;
  isHazardGame: boolean;
  scores: number[];
  leaders: boolean[];
  losers: boolean[];
  sharedLeaders: boolean[];
};

export const buildOrderedTeams = (teams: TeamSummary[]): TeamSummary[] => {
  if (!teams.length) return [];
  return [...teams].sort((a, b) => {
    const aKey = (a.name ?? a.id).toLowerCase();
    const bKey = (b.name ?? b.id).toLowerCase();
    return aKey.localeCompare(bKey);
  });
};

export const buildGameResultPlayers = (params: {
  players: Player[];
  orderedTeams: TeamSummary[];
  gameResults: GameResultState;
}): Player[] => {
  const { players, orderedTeams, gameResults } = params;

  if (players.length > 0) {
    if (!orderedTeams.length) return players;
    const playerById = new Map(players.map((p) => [p.id, p]));
    const ordered: Player[] = [];
    orderedTeams.forEach((team) => {
      (team.players ?? []).forEach((pid) => {
        const player = playerById.get(pid);
        if (player) ordered.push(player);
      });
    });
    const remaining = players.filter((p) => !ordered.some((op) => op.id === p.id));
    return [...ordered, ...remaining];
  }

  const keys = new Set<string>();
  Object.values(gameResults).forEach((game) => {
    Object.keys(game.scorePoints).forEach((pid) => keys.add(pid));
  });
  return Array.from(keys).map((k) => ({ id: k, name: k, createdAt: '' }));
};

export const buildDisplayTotals = (params: {
  leaderboardTotals: ScoreTotal[];
  totals: ScoreTotal[];
  gameResults: GameResultState;
  leaderboardGamePoints: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>>;
  players: Player[];
  tourTotals: Record<string, number>;
  unknownPlayerLabel: string;
}): DisplayTotal[] => {
  const { leaderboardTotals, totals, gameResults, leaderboardGamePoints, players, tourTotals, unknownPlayerLabel } = params;

  const sourceTotals = leaderboardTotals.length > 0 ? leaderboardTotals : totals;
  const normalizedSourceTotals = [...sourceTotals];
  const seenPlayerKeys = new Set<string>();
  normalizedSourceTotals.forEach((total) => {
    seenPlayerKeys.add(normalizePlayerKey(total.playerId ?? null));
    seenPlayerKeys.add(normalizePlayerKey(total.playerName ?? null));
  });
  players.forEach((player) => {
    const idKey = normalizePlayerKey(player.id);
    const nameKey = normalizePlayerKey(player.name);
    const alreadyPresent = seenPlayerKeys.has(idKey) || seenPlayerKeys.has(nameKey);
    if (alreadyPresent) {
      return;
    }
    normalizedSourceTotals.push({
      playerId: player.id,
      playerName: player.name,
      pickups: 0,
      totalStrokes: 0,
      totalRoundGamePoints: 0,
      tourGamesPoints: tourTotals[player.id] ?? 0,
      roundGamePoints: leaderboardGamePoints[player.id] ?? [],
    });
  });
  const useGameTotals = Object.keys(gameResults).length > 0;
  const playerTotals = new Map<string, number>();
  const leaderboardPointTotals = new Map<string, number>();

  normalizedSourceTotals.forEach((total) => {
    const match = players.find((p) => p.id === total.playerId || p.name === total.playerName);
    const key = total.playerId ?? match?.id ?? total.playerName ?? '';
    const scoreName = match?.name ?? total.playerName ?? total.playerId ?? '';

    if (useGameTotals) {
      let sum = 0;
      Object.values(gameResults).forEach((game) => {
        const idKey = normalizePlayerKey(key);
        const nameKey = normalizePlayerKey(scoreName);
        Object.entries(game.scorePoints).forEach(([candidateKey, value]) => {
          const norm = normalizePlayerKey(candidateKey);
          if (norm === idKey || norm === nameKey) {
            sum += value;
          }
        });
      });

      if (key) playerTotals.set(key, sum);
      if (scoreName) playerTotals.set(scoreName, sum);
    }
  });

  Object.entries(leaderboardGamePoints).forEach(([playerKey, points]) => {
    const totalPoints = points.reduce((sum, gp) => sum + (gp.roundGamePoints ?? 0), 0);
    leaderboardPointTotals.set(playerKey, totalPoints);
  });

  const resolveRoundPoints = (key: string, name: string) =>
    leaderboardPointTotals.get(key) ??
    leaderboardPointTotals.get(name) ??
    playerTotals.get(key) ??
    playerTotals.get(name) ??
    0;

  const sorted = [...normalizedSourceTotals].sort((a, b) => {
    const aKey = a.playerId ?? a.playerName ?? '';
    const bKey = b.playerId ?? b.playerName ?? '';
    const aScoreName = a.playerName ?? a.playerId ?? '';
    const bScoreName = b.playerName ?? b.playerId ?? '';
    const aScore = resolveRoundPoints(aKey, aScoreName);
    const bScore = resolveRoundPoints(bKey, bScoreName);
    return bScore - aScore;
  });

  const leaderScores = new Map<string, number>();
  sorted.forEach((total) => {
    const key = total.playerId ?? total.playerName ?? '';
    const scoreName = total.playerName ?? total.playerId ?? '';
    leaderScores.set(key, resolveRoundPoints(key, scoreName));
  });

  const scoreValues = Array.from(leaderScores.values());
  const leaderScore = scoreValues.length ? Math.max(...scoreValues) : 0;
  const hasLeaderData = scoreValues.some((score) => score !== 0);
  const leaderKeys = new Set(
    Array.from(leaderScores.entries())
      .filter(([, score]) => score === leaderScore)
      .map(([key]) => key)
  );
  const distinctScoreCount = new Set(leaderScores.values()).size;
  const hasDistinctLeader = distinctScoreCount > 1 && leaderKeys.size > 0;

  const roundRankByIdentity = new Map<string, number>();
  if (hasLeaderData && hasDistinctLeader) {
    let previousScore: number | null = null;
    let currentRank = 0;

    sorted.forEach((item, index) => {
      const itemKey = item.playerId ?? item.playerName ?? '';
      const itemScoreName = item.playerName ?? item.playerId ?? '';
      const itemScore = resolveRoundPoints(itemKey, itemScoreName);
      if (previousScore === null || itemScore !== previousScore) {
        currentRank = index + 1;
        previousScore = itemScore;
      }
      roundRankByIdentity.set(itemKey, currentRank);
      roundRankByIdentity.set(itemScoreName, currentRank);
    });
  }

  return sorted.map((total) => {
    const match = players.find((p) => p.id === total.playerId || p.name === total.playerName);
    const key = total.playerId ?? match?.id ?? total.playerName;
    const scoreName = match?.name ?? total.playerName ?? total.playerId ?? '';
    const candidateName = match?.name ?? total.playerName ?? '';
    const displayName =
      candidateName && !isUuidLike(candidateName)
        ? candidateName
        : scoreName && !isUuidLike(scoreName)
          ? scoreName
          : unknownPlayerLabel;
    const roundPoints = useGameTotals ? resolveRoundPoints(key ?? '', scoreName) : undefined;
    const tourPoints = tourTotals[key ?? ''] ?? tourTotals[scoreName];
    const isLeader = hasLeaderData && hasDistinctLeader && (leaderKeys.has(key ?? '') || leaderKeys.has(scoreName ?? ''));
    const rank = roundRankByIdentity.get(key ?? '') ?? roundRankByIdentity.get(scoreName);

    return {
      key: key ?? scoreName,
      name: displayName,
      totalStrokes: tourPoints ?? 0,
      roundPoints,
      pickups: total.pickups ?? 0,
      isLeader,
      roundRank: rank,
    };
  });
};

export const buildTourLeaderKeys = (displayTotals: DisplayTotal[]): Set<string> => {
  let maxPoints = -Infinity;
  displayTotals.forEach((total) => {
    if (typeof total.totalStrokes !== 'number') return;
    if (total.totalStrokes > maxPoints) maxPoints = total.totalStrokes;
  });

  if (!Number.isFinite(maxPoints) || maxPoints <= 0) return new Set<string>();

  const keys = new Set<string>();
  displayTotals.forEach((total) => {
    if (total.totalStrokes === maxPoints) {
      keys.add(normalizePlayerKey(total.key));
      keys.add(normalizePlayerKey(total.name));
    }
  });
  return keys;
};

export const buildTourPlacementByKey = (displayTotals: DisplayTotal[]): Record<string, number> => {
  const map: Record<string, number> = {};
  const ranked = [...displayTotals]
    .filter((total) => typeof total.totalStrokes === 'number')
    .sort((a, b) => b.totalStrokes - a.totalStrokes);

  let previousPoints: number | null = null;
  let currentRank = 0;
  ranked.forEach((total, index) => {
    if (previousPoints === null || total.totalStrokes !== previousPoints) {
      currentRank = index + 1;
      previousPoints = total.totalStrokes;
    }
    if (currentRank > 3) return;
    const keyNorm = normalizePlayerKey(total.key);
    const nameNorm = normalizePlayerKey(total.name);
    map[keyNorm] = currentRank;
    map[nameNorm] = currentRank;
  });

  return map;
};

export const buildRoundPlacementByKey = (displayTotals: DisplayTotal[]): Record<string, number> => {
  const map: Record<string, number> = {};
  displayTotals.forEach((total) => {
    if (!total.roundRank || total.roundRank > 3) return;
    const keyNorm = normalizePlayerKey(total.key);
    const nameNorm = normalizePlayerKey(total.name);
    map[keyNorm] = total.roundRank;
    map[nameNorm] = total.roundRank;
  });
  return map;
};

export const buildGameResultRows = (params: {
  orderedTeams: TeamSummary[];
  players: Player[];
  selectedGameDefinitionIds: string[];
  gameResults: GameResultState;
  leaderboardGamePoints: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>>;
  gameDefinitionNameById: Record<string, string>;
  gameDefinitionTypes: Record<string, string>;
  gameResultPlayers: Player[];
  gameFallbackName: string;
}): GameResultRow[] => {
  const {
    orderedTeams,
    players,
    selectedGameDefinitionIds,
    gameResults,
    leaderboardGamePoints,
    gameDefinitionNameById,
    gameDefinitionTypes,
    gameResultPlayers,
    gameFallbackName,
  } = params;

  const teamIdByPlayerKey = new Map<string, string>();
  orderedTeams.forEach((team) => {
    (team.players ?? []).forEach((pid) => {
      teamIdByPlayerKey.set(normalizePlayerKey(pid), team.id);
    });
  });

  players.forEach((player) => {
    const teamId = teamIdByPlayerKey.get(normalizePlayerKey(player.id));
    if (teamId) {
      teamIdByPlayerKey.set(normalizePlayerKey(player.name), teamId);
    }
  });

  const gameIds = new Set<string>();
  selectedGameDefinitionIds.forEach((id) => {
    if (!id) return;
    gameIds.add(id);
  });
  Object.keys(gameResults).forEach((id) => {
    if (!id) return;
    gameIds.add(id);
  });
  Object.values(leaderboardGamePoints).forEach((points) => {
    points.forEach((gp) => {
      if (!gp.gameId) return;
      gameIds.add(gp.gameId);
    });
  });

  const leaderboardPointsByPlayer = new Map<string, Record<string, number>>();
  Object.entries(leaderboardGamePoints).forEach(([playerKey, points]) => {
    const pointMap: Record<string, number> = {};
    points.forEach((gp) => {
      if (!gp.gameId) return;
      pointMap[gp.gameId] = gp.roundGamePoints;
    });
    leaderboardPointsByPlayer.set(normalizePlayerKey(playerKey), pointMap);
  });

  const rows = Array.from(gameIds).map((gameId) => {
    const game = gameResults[gameId];
    const gameName = game?.name ?? gameDefinitionNameById[gameId] ?? gameFallbackName;
    const isHazardGame = hazardTypeFromName(gameName) !== null;
    const scores = gameResultPlayers.map((player) => {
      const idKey = normalizePlayerKey(player.id);
      const nameKey = normalizePlayerKey(player.name);
      let score = 0;

      if (game?.scorePoints) {
        Object.entries(game.scorePoints).forEach(([key, value]) => {
          const norm = normalizePlayerKey(key);
          if (norm === idKey || norm === nameKey) {
            score = value;
          }
        });
      } else {
        const idPoints = leaderboardPointsByPlayer.get(idKey);
        const namePoints = leaderboardPointsByPlayer.get(nameKey);
        score = idPoints?.[gameId] ?? namePoints?.[gameId] ?? 0;
      }

      return score;
    });

    const maxScore = scores.length > 0 ? Math.max(...scores) : 0;
    const leaderFlags = scores.map((score) => score === maxScore && maxScore > 0);
    const minScore = scores.length > 0 ? Math.min(...scores) : 0;
    const loserFlags = isHazardGame ? scores.map((score) => score === minScore && minScore < 0) : scores.map(() => false);
    const isTeamGame = gameDefinitionTypes[gameId] === 'team';

    let sharedLeaders = leaderFlags.map(() => false);
    if (maxScore > 0) {
      if (isTeamGame && orderedTeams.length > 0) {
        const leaderTeamIds = new Set<string>();
        leaderFlags.forEach((isLeader, idx) => {
          if (!isLeader) return;
          const player = gameResultPlayers[idx];
          const teamId =
            teamIdByPlayerKey.get(normalizePlayerKey(player?.id)) ??
            teamIdByPlayerKey.get(normalizePlayerKey(player?.name ?? ''));
          if (teamId) {
            leaderTeamIds.add(teamId);
          }
        });
        const shared = leaderTeamIds.size > 1;
        if (shared) {
          sharedLeaders = leaderFlags.map((isLeader) => isLeader);
        }
      } else {
        const shared = leaderFlags.filter(Boolean).length > 1;
        if (shared) {
          sharedLeaders = leaderFlags.map((isLeader) => isLeader);
        }
      }
    }

    return {
      key: gameId,
      name: gameName,
      isHazardGame,
      scores,
      leaders: leaderFlags,
      losers: loserFlags,
      sharedLeaders,
    };
  });

  return rows.filter((row) => {
    const hasKnownName = row.name.trim().toLowerCase() !== gameFallbackName.trim().toLowerCase();
    const hasNonZeroScore = row.scores.some((score) => score !== 0);
    return hasKnownName || hasNonZeroScore;
  });
};

export const buildMysteryWinnersBySubgame = (params: {
  holeEvents: HoleEventEntry[];
  holeNumber: number;
}): Record<string, { playerId: string; occurredAt: string | null }> => {
  const { holeEvents, holeNumber } = params;
  const map: Record<string, { playerId: string; occurredAt: string | null }> = {};

  holeEvents
    .filter((event) => event.eventType === 'mysteryWinner' && event.holeNumber === holeNumber)
    .forEach((event) => {
      if (!event.eventValue) return;
      map[event.eventValue] = {
        playerId: event.playerId,
        occurredAt: event.occurredAt ?? null,
      };
    });

  return map;
};
